import {AuthForm, AuthHeading, AuthSubTitle, AuthTitle, AuthWrapper} from '@/components/ui/auth-atoms/AuthAtoms'
import i18n, {useTranslation} from '@/translations/i18n'
import {useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {useTheme} from 'styled-components'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {routes} from '@/utilities/constants/routes'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useNavigate} from 'react-router-dom'
import {useCallback, useEffect, useState} from 'react'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useSetPassword} from '@/features/auth/services/useSetPassword'
import {errorHandler} from '@/utilities/helpers'
import {useVerifyInviteToken} from '@/features/auth/services/useVerifyInviteToken'

const SetPasswordByInviteSearchParams = z.object({token: z.string().trim()})
type SetPasswordByInviteSearchParams = z.infer<typeof SetPasswordByInviteSearchParams>

const SetPasswordByInviteValidationSchema = z
    .object({
        password: z.string().min(8, i18n.t('commons:password_rules')),
        confirmPassword: z.string()
    })
    .superRefine(({password, confirmPassword}, ctx) => {
        if (confirmPassword != password) {
            ctx.addIssue({
                code: 'custom',
                message: i18n.t('errors:confirm_password'),
                path: ['confirmPassword']
            })
        }
    })
type SetPasswordByInviteValidationSchema = z.infer<typeof SetPasswordByInviteValidationSchema>

export const SetPasswordByInvite = () => {
    const {t} = useTranslation()
    const [verifiedToken, setVerifiedToken] = useState<boolean | null>(null)
    const {searchParams} = useParsedSearchParams(SetPasswordByInviteSearchParams)
    const {palette} = useTheme()
    const navigate = useNavigate()

    const form = useForm<SetPasswordByInviteValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(SetPasswordByInviteValidationSchema)
    })

    // mutations
    const verifyRecoveryToken = useVerifyInviteToken({
        onSuccess: () => setVerifiedToken(true),
        onError: () => setVerifiedToken(false)
    })
    const setPasswordByInvite = useSetPassword({
        onSuccess: () => navigate(routes.LOGIN.path, {replace: true}),
        onError: error => errorHandler(error)
    })

    // form values
    const password = useWatch({control: form.control, name: 'password'})
    const confirmPassword = useWatch({control: form.control, name: 'confirmPassword'})

    // verify token on first access
    const runTokenVerification = useCallback((token: string) => {
        verifyRecoveryToken.mutate({token})
    }, [])

    useEffect(() => {
        runTokenVerification(searchParams.token)
    }, [searchParams.token])

    // form submission
    const handleSubmit = form.handleSubmit(() =>
        setPasswordByInvite.mutate({
            password,
            passwordConfirmation: confirmPassword,
            token: searchParams.token
        })
    )

    return (
        <AuthWrapper>
            {verifiedToken === null || verifyRecoveryToken.isPending ? (
                <Spinner size={40} />
            ) : (
                <AuthForm fullWidth align="center" justify="center" gap={8} onSubmit={handleSubmit}>
                    {verifiedToken === false ? (
                        <Flexbox fullWidth direction="column" gap={6} align="center">
                            <AlertCircleIcon size={32} color={palette.danger[500]} />
                            <Flexbox direction="column" gap={2} align="center">
                                <AuthTitle>{t('commons:invalid_link')}</AuthTitle>
                                <AuthSubTitle>{t('set_password:subtitle_error')}</AuthSubTitle>
                                <AuthSubTitle>{t('set_password:error_cta')}</AuthSubTitle>
                            </Flexbox>
                        </Flexbox>
                    ) : (
                        <>
                            <AuthHeading
                                showLogo
                                title={t('set_password:title')}
                                subtitle={t('set_password:subtitle')}
                            />
                            <Flexbox direction="column" gap={4} fullWidth>
                                <InputText
                                    type="password"
                                    label={t('commons:password')}
                                    placeholder={'••••••••••'}
                                    errorMessage={form.formState.errors.password?.message}
                                    helpText={t('commons:password_rules')}
                                    visibilityToggle
                                    {...form.register('password')}
                                />
                                <InputText
                                    type="password"
                                    label={t('commons:repeat_password')}
                                    placeholder={'••••••••••'}
                                    visibilityToggle
                                    touched={form.formState.isDirty}
                                    errorMessage={form.formState.errors.confirmPassword?.message}
                                    successMessage={
                                        form.formState.isValid && password == confirmPassword
                                            ? t('commons:password_match')
                                            : ''
                                    }
                                    {...form.register('confirmPassword')}
                                />
                            </Flexbox>
                            <Button type="submit" fullWidth variant="primaryNeutral">
                                {t('commons:login')}
                            </Button>
                        </>
                    )}
                </AuthForm>
            )}
        </AuthWrapper>
    )
}

SetPasswordByInvite.displayName = 'SetPasswordByInvite'
